// frontend/src/utils/chartOptions.js

// [Clean Code] Y축 AxisPointer에 적용할 공통 스타일 (정수 + 콤마)
// X축(날짜)에는 영향을 주지 않기 위해 별도 상수로 관리합니다.
const Y_AXIS_POINTER_STYLE = {
    label: {
        formatter: (params) => Math.round(params.value).toLocaleString()
    }
};

/**
 * ECharts 인스턴스를 위한 범용 기본 설정을 생성합니다.
 */
export const createChartOptions = ({ series, legend, xAxis, yAxis, grid, dataZoom, tooltip, title, toolbox }) => {
    const effectiveDataZoom = dataZoom ? dataZoom.map(dz => {
        if (dz.type === 'inside') {
            return { ...dz, disabled: true }; 
        }
        return dz;
    }) : [
        { type: 'inside', disabled: true }, 
        { type: 'slider', show: true, bottom: '5rem' } 
    ];

    return {
        backgroundColor: '#ffffff',
        title: title,
        tooltip: tooltip || 
                { 
                    trigger: 'axis', 
                    axisPointer: { type: 'cross' },
                    valueFormatter: (value) => (value == null || isNaN(value)) ? 'N/A' : value.toLocaleString()
                },
        legend: legend || { data: series.map(s => s.name), top: '15rem' },
        grid: grid || {
            top: '75rem',
            bottom: '50rem',
            left: '30rem',
            right: '30rem',
            containLabel: true,
        },
        xAxis: xAxis,
        yAxis: yAxis,
        dataZoom: effectiveDataZoom,
        toolbox: toolbox,
        series: series.map(s => ({
             ...s, 
            data : s.data,
            connectNulls: true, 
            showSymbol: s.showSymbol !== undefined ? s.showSymbol : false 
        })), 
    };
};

/**
 * ExportVsStockPage용 차트 옵션
 * - 기존 로직 유지, Y축 포맷터만 공통 상수로 교체하여 정리
 */
export const createDualAxisChartOptions = ({ categories, lineData, barData }) => {
    const leftColor = '#5470C6'; 
    const rightColor = '#FFD32C99'; 

    const yAxisConfig = [
        {
            type: 'log',
            name: lineData.name,
            position: 'left',
            nameTextStyle: { color: leftColor },
            axisLine: { show: true, lineStyle: { color: leftColor } },
            axisLabel: { color: leftColor, align: 'right' },
            scale : true,
            axisPointer: Y_AXIS_POINTER_STYLE // [적용] 정수+콤마
        },
        {
            type: 'value',
            name: barData.name,
            position: 'right',
            nameTextStyle: { color: rightColor },
            axisLine: { show: true, lineStyle: { color: rightColor } },
            axisLabel: { color: rightColor, align: 'left' },
            scale : false,
            splitLine: {
                show: false,
                lineStyle: { type: 'dotted', color: 'gray' }
            },
            axisPointer: Y_AXIS_POINTER_STYLE // [적용] 정수+콤마
        }
    ];

    const seriesConfig = [
        {
            name: lineData.name,
            type: 'line',
            yAxisIndex: 0, 
            itemStyle: { color: leftColor },
            data: lineData.data
        },
        {
            name: barData.name,
            type: 'bar',
            yAxisIndex: 1, 
            itemStyle: { color: rightColor },
            data: barData.data
        }
    ];

    return createChartOptions({
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
        },
        legend: {
            data: [lineData.name, barData.name],
            top: '20rem'
        },
        xAxis: {
            type: 'category',
            data: categories,
            axisPointer: { type: 'shadow' }
        },
        yAxis: yAxisConfig,
        series: seriesConfig,
        grid: {
            top: '100rem',
            bottom: '50rem',
            left: '30rem',
            right: '30rem',
            containLabel: true 
        }
    });
};

/**
 * ExportChartByRegionPage용 동적 차트 옵션
 * - 복잡한 Unit 로직은 유지하되, AxisPointer 스타일은 공통 상수로 통일
 */
export const createDynamicDualAxisOptions = ({ leftAxis, rightAxis, title, dataZoom }) => {
    const darkGrayColor = '#666';
    const yAxes = [];
    const seriesConfig = [];
    const legendData = [];

    // --- 1. 왼쪽 축 설정 ---
    if (leftAxis && leftAxis.series.length > 0) {
        yAxes.push({
            type: leftAxis.type || 'value',
            name: leftAxis.name,
            position: 'left',
            nameTextStyle: { color: darkGrayColor, padding: [0, 0, 0, 0] },
            axisLine: { show: true, lineStyle: { color: darkGrayColor } },
            // 축의 눈금 라벨(Axis Label)은 데이터 특성(%, $M)에 따라 다르게 표시
            axisLabel: { 
                color: darkGrayColor,
                formatter: (value) => {
                    if (leftAxis.type === 'log' || leftAxis.isStock) {
                        return `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
                    }
                    if (leftAxis.unit === '%, YoY') {
                        return `${value.toFixed(1)}%`;
                    }
                    return `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
                }
            },
            scale: true,
            splitLine: { show: false },
            // 마우스 오버 시 나오는 라벨(Axis Pointer)은 무조건 정수+콤마
            axisPointer: Y_AXIS_POINTER_STYLE 
        });

        leftAxis.series.forEach(s => {
            legendData.push(s.name);
            seriesConfig.push({
                ...s,
                yAxisIndex: 0,
                type: s.type || 'line',
            });
        });
    }

    // --- 2. 오른쪽 축 설정 ---
    if (rightAxis && rightAxis.series.length > 0) {
        const rightAxisIndex = yAxes.length; 
        yAxes.push({
            type: 'value',
            name: rightAxis.name,
            position: 'right',
            nameTextStyle: { color: darkGrayColor, padding: [0, 0, 0, 0] },
            axisLine: { show: true, lineStyle: { color: darkGrayColor } },
            axisLabel: { 
                color: darkGrayColor,
                formatter: (value) => rightAxis.unit === '%, YoY'
                    ? `${value.toFixed(1)}%`
                    : `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
            },
            scale: true,
            splitLine: { show: false },
            // 마우스 오버 시 나오는 라벨(Axis Pointer)은 무조건 정수+콤마
            axisPointer: Y_AXIS_POINTER_STYLE 
        });

        rightAxis.series.forEach(s => {
            legendData.push(s.name);
            seriesConfig.push({
                ...s,
                yAxisIndex: rightAxisIndex,
                type: s.type || 'line'
            });
        });
    }

    // --- 3. 툴팁 포맷터 ---
    const tooltipFormatter = (params) => {
        if (!params || params.length === 0) return '';
        const date = params[0].axisValueLabel;
        let tooltipContent = `${date}<br/>`;

        params.forEach(param => {
            const seriesName = param.seriesName;
            const rawValue = (param.value && param.value.length > 1) ? param.value[1] : null;
            
            let formattedValue = '-';
            if (rawValue != null && !isNaN(rawValue)) {
                const isLeft = seriesConfig.find(s => s.name === seriesName)?.yAxisIndex === 0;
                const targetAxis = isLeft ? leftAxis : rightAxis;

                if (targetAxis?.isStock) {
                    formattedValue = `${rawValue.toLocaleString()}원`;
                } else if (targetAxis?.unit === '%, YoY') {
                    formattedValue = `${rawValue.toFixed(1)}%`;
                } else {
                    formattedValue = `$${(rawValue / 1000).toFixed(2)}M`;
                }
            }
            tooltipContent += `${param.marker} ${seriesName}: <b>${formattedValue}</b><br/>`;
        });
        return tooltipContent;
    };

    return createChartOptions({
        title,
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: tooltipFormatter
        },
        legend: { data: legendData, top: '50rem', type: 'scroll' },
        xAxis: { type: 'time', splitLine: { show: false } },
        yAxis: yAxes,
        series: seriesConfig,
        grid: { top: '80rem', bottom: '50rem', left: '20rem', right: '20rem', containLabel: true },
        dataZoom: dataZoom,
        toolbox: { show: false }
    });
};